import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { saveAs } from 'file-saver';

@Injectable({
  providedIn: 'root',
})
export class DataManagementAppService {
  private activeRouteSubject = new BehaviorSubject<string>('');
  activeRoute$ = this.activeRouteSubject.asObservable();

  configuringDataSourceName: string = '';
  private configuredTypeData: string = '';
  destinationTableName: string = '';
  constructor(private http: HttpClient, private router: Router) {}

  getPythonDateFormats() {
    const url = '/data_management_app/python-date-formats';
    return this.http.get(url);
  }
  setDataSourceName(dataName: string) {
    this.configuringDataSourceName = dataName;
  }

  getDataSourceName(): string {
    return this.configuringDataSourceName;
  }

  setDestinationTableName(dataName: string) {
    this.destinationTableName = dataName;
  }

  getDestinationTableName(): string {
    return this.destinationTableName;
  }

  setDataType(data: string) {
    this.configuredTypeData = data;
  }
  getDataSetType() {
    return this.configuredTypeData;
  }

  getDataPreview(dataSetName: string) {
    const url = `/data_management_app/cache/data-management/${dataSetName.toLowerCase()}/preview?parse=t`;
    return this.http.get(url);
  }

  getMapping(dataSetName: string): Observable<any> {
    const url = `/data_management_app/cache/data-management/${dataSetName.toLowerCase()}/create_mapping?parse=t`;
    return this.http.get(url);
  }

  applyMapping(dataSetName: string, data: Object) {
    const url = `/data_management_app/cache/data-management/${dataSetName.toLowerCase()}/apply_mapping?parse=t`;
    return this.http.post(url, data);
  }

  getDescriptives(dataSetName: string) {
    const url = `/data_management_app/cache/data-management/${dataSetName.toLowerCase()}/descriptives?parse=t`;
    return this.http.get(url);
  }

  getProfiling(dataSetName: string) {
    const url = `/data_management_app/cache/data-management/${dataSetName.toLowerCase()}/profile_report?parse=t`;
    return this.http.get(url, { responseType: 'text' });
  }

  getSavedData(dataSetName: string) {
    const url = `/data_management_app/cache/data-management/${dataSetName.toLowerCase()}/save?parse=t`;
    return this.http.get(url);
  }

  getConnections(connectionFor: string): Observable<any> {
    const url = ` /data_management_app/data-connection?${connectionFor}=t`;
    return this.http.get(url);
  }

  save_connections(connection: any, is_edit: boolean = false) {
    const url = '/data_management_app/data-connection';
    if (is_edit) return this.http.put(url, connection);
    return this.http.post(url, connection);
  }

  test_connection(connection: any): Observable<any> {
    const url = '/data_management_app/data-connection/test';
    return this.http.post(url, connection);
  }

  delete_connections(connection: any) {
    const url = '/data_management_app/data-connection';
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: connection,
    };
    return this.http.delete(url, options);
  }

  async getConnectionTemplate(connectionType: string): Promise<any> {
    const url = `/data_management_app/connection-type/template/${connectionType}`;
    const result = await fetch(url);
    return result.json();
  }

  getDataManagemententry(dataSetName: string): Observable<any> {
    const url = `/data_management_app/data-management/${dataSetName}`;
    return this.http.get(url);
  }
  async getDataManagemententry1(dataSetName: string): Promise<any> {
    const url = `/data_management_app/data-management/${dataSetName.toLocaleLowerCase()}`;
    const result1 = await fetch(url);
    return result1.json();
  }
  putDataManagemententry(data: {}, dataSetName: string): Observable<any> {
    const url = `/data_management_app/data-management/${dataSetName.toLowerCase()}`;
    return this.http.post(url, data);
  }

  deleteDataManagemententry(name: string): Observable<any> {
    const url = `/data_management_app/data-management/${name}`;
    return this.http.delete(url);
  }

  async generateData(dataSetName: string, nodeTag: string): Promise<any> {
    const url = `/data_management_app/data-management/${dataSetName.toLowerCase()}?tags=${nodeTag}&pipeline_run=t`;

    return fetch(url)
      .then((result) => result.json())
      .catch((error) => {
        console.error('Error fetching or parsing JSON:', error);
        return Promise.reject(error);
      });
  }

  getCacheStatus(dataSetName: string, cacheStepName: string) {
    const url = `/data_management_app/cache/data-management/${dataSetName.toLowerCase()}/${cacheStepName}?is_exist=t`;
    return this.http.get(url);
  }

  deleteCache(dataSetName: string, stepName: string) {
    const url = `/data_management_app/cache/data-management/${dataSetName.toLowerCase()}/${stepName}?parse=t`;
    return this.http.delete(url, { responseType: 'text' });
  }

  getConfiguredDataSource() {
    const url = '/data_management_app/data-management';
    return this.http.get(url);
  }

  downloadCsv(datasetName: string): Observable<Blob> {
    const url = `/data_management_app/data-management/${datasetName}?mapped_csv=true`;
    return this.http.get(url, { responseType: 'blob' });
  }

  saveCsvFile(blob: Blob, fileName: string): void {
    saveAs(blob, fileName);
  }

  downloadDatasetFiles(dataName: string) {
    const url = `data_management_app/upload_files/${dataName}/download/`;
    return this.http.get(url, { responseType: 'blob', observe: 'response' });
  }

  downloadSingleFile(dataName: string, fileName: string): Observable<Blob> {
    const url = `data_management_app/upload_files/${dataName}/${encodeURIComponent(
      fileName
    )}/download/`;
    return this.http.get(url, { responseType: 'blob' });
  }

  getUploadFilesConfiguration() {
    const url = 'data_management_app/upload_files_configuration/';
    return this.http.get(url);
  }

  getUploadedFiles(dataName: string) {
    const url = `data_management_app/upload_files/${dataName}/`;
    return this.http.get(url);
  }

  uploadFiles(files: File[], dataName: string, updateFlag = false) {
    const lastUrl = updateFlag ? `${dataName}/` : '';
    const url = `data_management_app/upload_files/${lastUrl}`;

    const formData = new FormData();
    formData.append('dataName', dataName);

    files.forEach((file, index) => formData.append(`file${index}`, file));

    return this.http.post(url, formData);
  }

  deleteUploadedFiles(dataName: string) {
    const url = `data_management_app/upload_files/${dataName}/`;
    return this.http.delete(url);
  }

  deleteSingleFile(dataName: string, fileUrl: string) {
    const url = `data_management_app/upload_files/${dataName}/${fileUrl}/`;
    return this.http.delete(url);
  }
}
